import { clientMeasurementRows, measurementProfileOrder, measurementProfiles, type ClientMeasurementId, type MeasurementProfile, type MeasurementProfileId, type SizeRange } from "@tailor-frame/measurements";
import type { PatternSpecification } from "@tailor-frame/engine";

export type BodyProfileId = Exclude<MeasurementProfileId, "small" | "medium" | "large" | "x-large">;

export const bodyProfileIds: BodyProfileId[] = measurementProfileOrder.filter((id): id is BodyProfileId => !["small", "medium", "large", "x-large"].includes(id));

/** The sizes a profile is for. */
export function profileSizeRange(profileId: BodyProfileId): SizeRange {
  return measurementProfiles[profileId].sizeRange ?? "adult";
}

/** The sizes a pattern is drafted for: adult unless the spec says otherwise. */
export function specificationSizeRange(specification: PatternSpecification): SizeRange {
  return specification.pattern.sizeRange ?? "adult";
}

/** Profiles a pattern can be drafted for: those in its size range. */
export function profileIdsFor(specification: PatternSpecification): BodyProfileId[] {
  const range = specificationSizeRange(specification);
  return bodyProfileIds.filter((id) => profileSizeRange(id) === range);
}

/** The profile a new project for a pattern starts from: Chris, or the first in the pattern's size range. */
export function defaultProfileFor(specification: PatternSpecification): BodyProfileId {
  return specificationSizeRange(specification) === "adult" ? "chris" : profileIdsFor(specification)[0];
}

// Spec measurement ID -> profile measurement IDs, first defined value wins.
const measurementAliases: Record<string, string | string[]> = {
  chest: "chest",
  scyeDepth: "scyeDepth",
  backNeckToWaist: "backNeckToWaist",
  neck: "neck",
  halfBack: "halfBack",
  shirtLength: "shirtLength",
  jacketLength: "jacketLength",
  coatLength: "coatLength",
  overcoatLength: "coatLength",
  capeLength: "coatLength",
  sleeveLength: "onePieceSleeveLength",
  shirtSleeveLength: ["shirtSleeveLength", "twoPieceSleeveLength"],
  seat: "seat",
  naturalWaist: "naturalWaist",
  trouserWaist: "trouserWaist",
  bodyRise: "bodyRise",
  insideLeg: "insideLeg",
  finishedLength: "shirtLength",
  shortSleeveLength: "shortOnePieceSleeveLength",
  closeWrist: "wrist",
  jeansBottomWidth: "trouserBottomWidth",
  trouserBottomWidth: "trouserBottomWidth",
  garmentLength: "shirtLength",
  naturalWaistLength: "backNeckToWaist",
  twoPieceSleeveLength: "twoPieceSleeveLength",
  // Women's wear. Aldrich's "chest" is frontChestWidth, never `chest` (a girth).
  bust: "bust",
  frontChestWidth: "frontChestWidth",
  bustDart: "bustDart",
  shoulder: "shoulder",
  frontShoulderToWaist: "frontShoulderToWaist",
  waistToHip: "waistToHip",
  waistToKnee: "waistToKnee",
  waistToFloor: "waistToFloor",
  skirtLength: "skirtLength",
  ankle: "ankle",
  highAnkle: "highAnkle",
  jerseySleeveLength: "jerseySleeveLength",
  // Children's wear. Aldrich's arm length (H–T) is the one-piece sleeve length.
  armLength: "onePieceSleeveLength",
  height: "height",
  cervicalHeight: "cervicalHeight",
  headCircumference: "headCircumference",
  verticalTrunk: "verticalTrunk",
  footLength: "footLength",
};

export function measurementsForProfile(specification: PatternSpecification, profileId: BodyProfileId): Record<string, number> {
  const profile = measurementProfiles[profileId];
  return Object.fromEntries(Object.entries(specification.measurements).map(([measurementId, definition]) => {
    const aliases = [measurementAliases[measurementId] ?? []].flat() as (keyof typeof profile.values)[];
    const valueCm = aliases.map((alias) => profile.values[alias]).find((value) => value !== undefined);
    return [measurementId, valueCm === undefined ? definition.default : valueCm * 10];
  }));
}

export function measurementProfileLabel(profileId: BodyProfileId): string {
  return measurementProfiles[profileId].label;
}

/** Standing height and head size for a profile, in mm, when it records them. */
export function measurementProfileStature(profileId: BodyProfileId) {
  return measurementProfiles[profileId].stature;
}

export function measurementProfileFigure(profileId: BodyProfileId): "male" | "female" {
  return measurementProfiles[profileId].figure;
}

/** One body measurement shared by every pattern, in millimetres. */
export interface BodyMeasurementRow {
  id: ClientMeasurementId;
  label: string;
  howToMeasure: string;
  /** "Women's" rows are only read by women's wear drafts, "Children's" mostly by children's wear. */
  group: "Body" | "Extra" | "Women's" | "Children's";
  minimum: number;
  maximum: number;
}

export type BodyMeasurementSet = Record<ClientMeasurementId, number>;

// Wide enough for a newborn (Aldrich's 56 cm babies) up to a large adult.
const bodyRanges: Record<ClientMeasurementId, [number, number]> = {
  backNeckToWaist: [150, 560], shirtLength: [250, 1100], jacketLength: [250, 1200], coatLength: [300, 1500], halfBack: [80, 300], chest: [400, 1500],
  naturalWaist: [400, 1400], trouserWaist: [400, 1400], seat: [400, 1600], neck: [200, 550],
  shoulder: [40, 220], onePieceSleeveLength: [180, 750], shortOnePieceSleeveLength: [50, 400],
  twoPieceSleeveLength: [250, 950], elbowLength: [100, 450], shirtSleeveLength: [250, 950],
  trouserSideSeam: [250, 1250], bodyRise: [100, 420], insideLeg: [150, 1000], trouserBottomWidth: [120, 350],
  wrist: [90, 240], armCircumference: [140, 500], scyeDepth: [90, 320],
  bust: [400, 1500], frontChestWidth: [150, 500], bustDart: [0, 160], frontShoulderToWaist: [150, 560],
  waistToHip: [70, 280], waistToKnee: [200, 750], waistToFloor: [250, 1250], skirtLength: [100, 1200],
  ankle: [100, 350], highAnkle: [100, 320], jerseySleeveLength: [150, 700],
  height: [450, 2200], cervicalHeight: [350, 1900], headCircumference: [300, 650], verticalTrunk: [500, 1900], footLength: [60, 350],
};

export const bodyMeasurementRows: BodyMeasurementRow[] = clientMeasurementRows.map((row) => ({
  id: row.id,
  label: row.measurement,
  howToMeasure: row.howToMeasure,
  group: row.group === "womens" ? "Women's" : row.group === "childrens" ? "Children's" : row.group === "extra" ? "Extra" : "Body",
  minimum: bodyRanges[row.id][0],
  maximum: bodyRanges[row.id][1],
}));

/**
 * Every body measurement for a saved profile, in mm. Values the profile does
 * not hold are estimated (from related measurements or the medium profile) and
 * listed in `estimated` so the page can say so.
 */
export function bodyMeasurementsForProfile(profileId: BodyProfileId): { values: BodyMeasurementSet; estimated: ClientMeasurementId[] } {
  return bodyMeasurementsFor(measurementProfiles[profileId]);
}

/** Every body measurement for any profile, such as a size chart's, estimated as for a saved one. */
export function bodyMeasurementsFor(profile: MeasurementProfile): { values: BodyMeasurementSet; estimated: ClientMeasurementId[] } {
  const saved = profile.values;
  const fallback = measurementProfiles.medium.values;
  const female = profile.figure === "female";
  const adult = (profile.sizeRange ?? "adult") === "adult";
  const tenth = (valueCm: number) => Math.round(valueCm * 10) / 10;
  // Derived lengths that other estimates build on.
  const insideLeg = saved.insideLeg ?? (saved.waistToFloor !== undefined && saved.bodyRise !== undefined ? saved.waistToFloor - saved.bodyRise : undefined);
  const twoPieceSleeveLength = saved.twoPieceSleeveLength ?? (saved.halfBack !== undefined && saved.onePieceSleeveLength !== undefined ? saved.halfBack + saved.onePieceSleeveLength - 2 : undefined);
  const waistToFloor = saved.waistToFloor ?? (insideLeg !== undefined && saved.bodyRise !== undefined ? insideLeg + saved.bodyRise : undefined);
  const waistToKnee = saved.waistToKnee ?? (waistToFloor !== undefined ? tenth(waistToFloor * 0.5625) : undefined);
  // Height from the profile's stature, else the default figure's 175 cm.
  const height = saved.height ?? (profile.stature ? profile.stature.height / 10 : adult ? 175 : undefined);
  // A girth stands in for the other (bust for chest, chest for bust) until measured; never as an alias.
  const bust = saved.bust ?? saved.chest;
  const estimates: Partial<Record<ClientMeasurementId, number>> = {
    chest: saved.bust,
    trouserWaist: saved.naturalWaist !== undefined ? saved.naturalWaist + 4 : undefined,
    insideLeg,
    twoPieceSleeveLength,
    shirtSleeveLength: twoPieceSleeveLength,
    elbowLength: saved.onePieceSleeveLength !== undefined ? Math.round(saved.onePieceSleeveLength * 0.52 * 10) / 10 : undefined,
    trouserSideSeam: insideLeg !== undefined && saved.bodyRise !== undefined ? insideLeg + saved.bodyRise : undefined,
    // Nape-to-hem lengths from the nape-to-waist length (about a quarter of the height).
    shirtLength: saved.backNeckToWaist !== undefined ? Math.round(saved.backNeckToWaist * 1.65) : undefined,
    jacketLength: saved.backNeckToWaist !== undefined ? Math.round(saved.backNeckToWaist * 1.75) : undefined,
    coatLength: saved.backNeckToWaist !== undefined ? Math.round(saved.backNeckToWaist * 2.2) : undefined,
    shortOnePieceSleeveLength: adult ? 22 : saved.onePieceSleeveLength !== undefined ? tenth(saved.onePieceSleeveLength * 0.35) : undefined,
    shoulder: female ? 12.25 : 15.5,
    armCircumference: bust !== undefined ? Math.round(bust * 0.33) : undefined,
    // Women's wear, graded from Aldrich's size 12 (p.11) by the bust or hips.
    bust,
    frontChestWidth: bust !== undefined ? tenth(32.4 + (bust - 88) * 0.3) : undefined,
    bustDart: bust !== undefined ? tenth(7 + (bust - 88) * 0.15) : undefined,
    frontShoulderToWaist: saved.backNeckToWaist,
    waistToHip: saved.seat !== undefined ? tenth(20.6 + (saved.seat - 94) * 0.075) : undefined,
    waistToFloor,
    waistToKnee,
    skirtLength: waistToKnee,
    ankle: bust !== undefined ? tenth(24 + (bust - 88) * 0.125) : undefined,
    highAnkle: saved.ankle !== undefined ? saved.ankle - 3 : bust !== undefined ? tenth(21 + (bust - 88) * 0.125) : undefined,
    jerseySleeveLength: saved.onePieceSleeveLength !== undefined ? saved.onePieceSleeveLength - 6 : undefined,
    // Children's wear rows for anyone the charts don't cover.
    height,
    cervicalHeight: height !== undefined ? tenth(height * 0.85) : undefined,
    // From the head's width and depth when known: round an ellipse.
    headCircumference: profile.stature ? tenth((Math.PI * (profile.stature.headWidth + profile.stature.headDepth)) / 20) : female ? 55 : 57,
    // About 2.5 times nape to waist plus body rise, as in the babies' chart (p. 36).
    verticalTrunk: saved.backNeckToWaist !== undefined && saved.bodyRise !== undefined ? tenth((saved.backNeckToWaist + saved.bodyRise) * 2.5) : undefined,
    // About 15% of the height, as in the babies' chart.
    footLength: height !== undefined ? tenth(height * 0.15) : undefined,
  };
  const estimated: ClientMeasurementId[] = [];
  const values = Object.fromEntries(clientMeasurementRows.map((row) => {
    const own = saved[row.id];
    if (own !== undefined) return [row.id, Math.round(own * 10)];
    estimated.push(row.id);
    const valueCm = profile.estimates?.[row.id] ?? estimates[row.id] ?? fallback[row.id] ?? (bodyRanges[row.id][0] + bodyRanges[row.id][1]) / 20;
    return [row.id, Math.round(valueCm * 10)];
  })) as BodyMeasurementSet;
  return { values, estimated };
}

/** The body measurement a pattern measurement reads, if it reads one. */
export function bodyMeasurementFor(measurementId: string): ClientMeasurementId | undefined {
  return [measurementAliases[measurementId] ?? []].flat()[0] as ClientMeasurementId | undefined;
}

/** Pattern measurements filled from the shared body set (only those linked to a body measurement). */
export function linkedMeasurements(specification: PatternSpecification, body: Partial<BodyMeasurementSet>): Record<string, number> {
  return Object.fromEntries(Object.keys(specification.measurements).flatMap((measurementId) => {
    const bodyId = bodyMeasurementFor(measurementId);
    const value = bodyId === undefined ? undefined : body[bodyId];
    return value === undefined ? [] : [[measurementId, value]];
  }));
}
